import { useState } from "react";
import { Box, useTheme } from "@mui/material";

import {
  Button,
  Dropdown,
  PopupDialog,
  PopupDialogTitle,
  TextField,
} from "~/components/design-system/index.js";

// Predefined reasons for declining a redo. The selected labels are joined into
// the single free-text `reason` passed to onConfirm, so there are no structured
// values to persist.
const declineReasons: { value: string; label: string }[] = [
  { value: "no-longer-required", label: "The redo is no longer required" },
  { value: "original-correct", label: "The original form is correct" },
  { value: "another-form-instead", label: "Another form will be used instead" },
  { value: "other", label: "Other" },
];

/**
 * Shown to the author who is declining a redo request so they must record why.
 * A presentational dialog: it collects the reasons and optional detail, then
 * hands them back so the caller owns the decline API call and navigation.
 */
export function DeclineRedoDialog({
  onClose,
  onConfirm,
  loading = false,
}: {
  onClose: () => void;
  onConfirm: (reason: string, reasonDescription: string | undefined) => void;
  loading?: boolean;
}) {
  const theme = useTheme();

  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [additionalInfo, setAdditionalInfo] = useState<string>("");

  // At least one reason must be selected; the additional information is optional.
  const canConfirm = !loading && !!selectedReasons.length;

  const confirm = () => {
    const reason = declineReasons
      .filter((r) => selectedReasons.includes(r.value))
      .map((r) => r.label)
      .join("; ");
    const trimmedInfo = additionalInfo.trim();
    onConfirm(reason, trimmedInfo || undefined);
  };

  return (
    <PopupDialog open={true} onClose={onClose}>
      <PopupDialogTitle
        titleText="Decline Redo Request"
        closeDialog={onClose}
      />
      <Dropdown
        label="Please select a reason why you are declining this redo"
        multiple
        name="declineReason"
        values={declineReasons}
        selectedValue={selectedReasons}
        onChange={(e: string[]) => {
          setSelectedReasons(e);
        }}
      />
      <Box sx={{ mt: 2 }}>
        <TextField
          name="additionalInfo"
          label="Please provide additional information"
          onChange={(e: string) => {
            setAdditionalInfo(e);
          }}
          value={additionalInfo}
          useFullWidth={true}
          multiline
          rows="5"
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          marginTop: theme.spacing(3),
        }}
      >
        <Button
          label={"Close"}
          onClick={onClose}
          variant="outlined"
          disabled={loading}
        />
        <Button
          color="primary"
          label={loading ? "Declining..." : "Confirm Decline"}
          disabled={!canConfirm}
          onClick={confirm}
        />
      </Box>
    </PopupDialog>
  );
}
